import React from "react";

import { faCheck, faExclamationTriangle, faSpinner } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faStop } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";

import { confirm } from "~/lib/prompts";
import { inVideocallMode } from "~/lib/utils";
import type { CopilotStatus as CopilotStatusType } from "~/stream/MeetingPhaseContext";
import { Tooltip } from "~/ui";
import Menu from "~/ui/menu";

import noteHeaderStyles from "../NoteHeader.module.css";
import styles from "./CopilotStatus.module.css";
import sharedStyles from "./RecordingControls.module.css";

export interface CopilotStatusProps {
	copilotStatus: Extract<
		CopilotStatusType,
		{ type: "starting" } | { type: "waiting" } | { type: "stopping" } | { type: "saved" } | { type: "error" }
	>;
	onCancelClicked: () => void;
}
type CopilotStatusTypeOptions = CopilotStatusProps["copilotStatus"]["type"];

const IconContents: { [key in CopilotStatusTypeOptions]: React.ReactNode } = {
	starting: <FontAwesomeIcon key="icon-starting" icon={faSpinner} fixedWidth pulse className={styles.icon} />,
	waiting: <FontAwesomeIcon key="icon-waiting" icon={faSpinner} fixedWidth pulse className={styles.icon} />,
	saved: <FontAwesomeIcon key="icon-saved" icon={faCheck} className={cx(styles.icon, styles.successIcon)} />,
	stopping: <FontAwesomeIcon key="icon-stopping" icon={faSpinner} fixedWidth pulse className={styles.icon} />,
	error: (
		<FontAwesomeIcon key="icon-error" icon={faExclamationTriangle} className={cx(styles.icon, styles.errorIcon)} />
	),
};

const TextContents: {
	[key in CopilotStatusTypeOptions]: string;
} = {
	starting: "Joining…",
	waiting: "Waiting…",
	saved: "Saved",
	stopping: "Saving…",
	error: "Error",
};

const BackgroundColors: { [key in CopilotStatusTypeOptions]?: string } = {
	saved: "var(--veryMutedGreen)",
	error: "var(--veryMutedRed)",
};

export default function CopilotStatus({ copilotStatus, onCancelClicked }: CopilotStatusProps) {
	const tooltipContents: { [key in CopilotStatusTypeOptions]?: string } = React.useMemo(
		() => ({
			starting: "The Note Taker will soon ask to join your call.",
			waiting: "Allow the Note Taker into your meeting from your video call provider.",
			error: copilotStatus.type === "error" ? copilotStatus.indicator.message : "",
		}),
		[copilotStatus],
	);

	const onCancelJoiningClick = React.useCallback(async () => {
		const confirmTitle = "Cancel the Note Taker?";
		const confirmText = (
			<p>
				Cancelling will prevent the Note Taker from joining the meeting and it will not be recorded. Are you sure you
				want to cancel the Note Taker?
			</p>
		);

		if (
			!(await confirm(confirmText, {
				yesText: "Cancel the Note Taker",
				noText: "Go back",
				yesProps: { variant: "destructive" },
				title: confirmTitle,
			}))
		) {
			return;
		}

		onCancelClicked();
	}, [onCancelClicked]);

	const canCancel = copilotStatus.type === "starting" || copilotStatus.type === "waiting";

	if (canCancel) {
		return (
			<Menu>
				<AnimatePresence>
					<Tooltip
						placement="top"
						maxWidth={150}
						className={sharedStyles.smallTooltip}
						content={tooltipContents[copilotStatus.type]}
						disabled={!Boolean(tooltipContents[copilotStatus.type])}
					>
						<Menu.Trigger asChild>
							<motion.button
								className={cx(
									noteHeaderStyles.ultraSlimButton,
									styles.status,
									styles.pointer,
									styles[copilotStatus.type],
								)}
								layout
								style={{ borderRadius: 6 }} // Prevents the border radius from being distorted during the animation
								initial={{ opacity: 0 }}
								animate={{
									backgroundColor: BackgroundColors[copilotStatus.type] || "var(--secondaryButtonColor)",
									opacity: 1,
								}}
								transition={{
									layout: {
										ease: "easeOut",
										duration: 0.3,
									},
								}}
								exit={{ opacity: 0 }}
							>
								<motion.span layout="position" style={{ display: "flex" }}>
									{IconContents[copilotStatus.type]}
								</motion.span>
								<motion.span
									className={cx(sharedStyles.buttonContent, { [sharedStyles.hideIfNoSpace]: !inVideocallMode })}
									layout="position"
								>
									{TextContents[copilotStatus.type]}
								</motion.span>
							</motion.button>
						</Menu.Trigger>
					</Tooltip>

					<Menu.Content key="cancel-menu-content" className={sharedStyles.recordingControlMenu} align="center">
						<Menu.Item onSelect={onCancelJoiningClick} className={sharedStyles.stopControlButton}>
							<span className={sharedStyles.recordingControlMenuItem}>
								<FontAwesomeIcon icon={faStop} className={sharedStyles.recordingControlButtonIcon} fixedWidth />
								Cancel
							</span>
						</Menu.Item>
					</Menu.Content>
				</AnimatePresence>
			</Menu>
		);
	}

	return (
		<Tooltip
			placement="top"
			maxWidth={150}
			className={sharedStyles.smallTooltip}
			content={tooltipContents[copilotStatus.type]}
			disabled={!Boolean(tooltipContents[copilotStatus.type])}
		>
			<AnimatePresence>
				<motion.span
					className={cx(noteHeaderStyles.ultraSlimButton, styles.status, styles[copilotStatus.type])}
					layout
					style={{ borderRadius: 6 }} // Prevents the border radius from being distorted during the animation
					initial={{ opacity: 0 }}
					animate={{
						backgroundColor: BackgroundColors[copilotStatus.type] || "var(--secondaryButtonColor)",
						opacity: 1,
					}}
					transition={{
						layout: {
							ease: "easeOut",
							duration: 0.3,
						},
					}}
					exit={{ opacity: 0 }}
				>
					<motion.span layout="position" style={{ display: "flex" }}>
						{IconContents[copilotStatus.type]}
					</motion.span>
					<motion.span
						className={cx(sharedStyles.buttonContent, { [sharedStyles.hideIfNoSpace]: !inVideocallMode })}
						layout="position"
					>
						{TextContents[copilotStatus.type]}
					</motion.span>
				</motion.span>
			</AnimatePresence>
		</Tooltip>
	);
}
